import React from 'react';
import { useNavigate } from 'react-router-dom';
import FloatingParticles from '../components/layout/FloatingParticles';
import RotatingCarousel from '../components/landing/RotatingCarousel';
import PricingSection from '../components/landing/PricingSection';
import ReviewsCarousel from '../components/rust/ReviewsCarousel';

interface RustPageProps {
  navigateToLogin: () => void;
}

const RustPage: React.FC<RustPageProps> = ({ navigateToLogin }) => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-950 bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 text-white flex flex-col">
      <FloatingParticles />

      {/* Main Content */}
      <div className="flex-1">
        <div className="pt-24 pb-16 px-4">
          <div className="max-w-7xl mx-auto">
            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left Column - Carousel and Reviews */}
              <div className="lg:col-span-2 space-y-8">
                {/* Showcase/Menu (Carousel) */}
                <div className="w-full h-[21.6rem] relative select-none" style={{ perspective: '2400px' }}>
                  <RotatingCarousel />
                </div>

                {/* Reviews Section */}
                <ReviewsCarousel />
              </div>

              {/* Right Column - Features List */}
              <div className="bg-gray-900/40 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50 shadow-[0_0_15px_rgba(0,0,0,0.3)] relative h-[34rem]">
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 rounded-xl" />
                <div className="relative h-full flex flex-col">
                  <h2 className="text-xl font-semibold text-blue-400 mb-6">Features</h2>
                  <div className="space-y-6 overflow-y-auto pr-4 custom-scrollbar flex-1 scrollbar-thin scrollbar-track-gray-900/40 scrollbar-thumb-blue-500/50">
                    {/* Recoil Section */}
                    <div>
                      <h3 className="text-lg font-medium text-gray-300 mb-3">Recoil</h3>
                      <ul className="space-y-2">
                        <li className="flex items-start">
                          <span className="text-blue-400 mr-2">•</span>
                          <span className="text-gray-300">Advanced recoil compensation</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-blue-400 mr-2">•</span>
                          <span className="text-gray-300">Customizable settings per weapon</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-blue-400 mr-2">•</span>
                          <span className="text-gray-300">Automatic weapon detection</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-blue-400 mr-2">•</span>
                          <span className="text-gray-300">Automatic scope detection</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-blue-400 mr-2">•</span>
                          <span className="text-gray-300">Humanization options</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-blue-400 mr-2">•</span>
                          <span className="text-gray-300">Old recoil support</span>
                        </li>
                      </ul>
                    </div>

                    {/* Misc Section */}
                    <div>
                      <h3 className="text-lg font-medium text-gray-300 mb-3">Misc</h3>
                      <ul className="space-y-2">
                        <li className="flex items-start">
                          <span className="text-blue-400 mr-2">•</span>
                          <span className="text-gray-300">Auto-Fire</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-blue-400 mr-2">•</span>
                          <span className="text-gray-300">Auditory notifications</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-blue-400 mr-2">•</span>
                          <span className="text-gray-300">Anti-AFK</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-blue-400 mr-2">•</span>
                          <span className="text-gray-300">Anti-Scriptcheck</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-blue-400 mr-2">•</span>
                          <span className="text-gray-300">Customizable menu colors</span>
                        </li>
                      </ul>
                    </div>

                    {/* Settings Section */}
                    <div>
                      <h3 className="text-lg font-medium text-gray-300 mb-3">Settings</h3>
                      <ul className="space-y-2">
                        <li className="flex items-start">
                          <span className="text-blue-400 mr-2">•</span>
                          <span className="text-gray-300">Config system</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-blue-400 mr-2">•</span>
                          <span className="text-gray-300">Automatic sensivity detection</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-blue-400 mr-2">•</span>
                          <span className="text-gray-300">Automatic FOV detection</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-blue-400 mr-2">•</span>
                          <span className="text-gray-300">Automatic resolution detection</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-blue-400 mr-2">•</span>
                          <span className="text-gray-300">Customizable keybinds</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Pricing Section */}
            <div className="mt-16">
              <PricingSection isRustPage={true} />
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-gray-800/50 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-gray-500 text-sm mb-4 md:mb-0">
              &copy; recoil.club 2025. All rights reserved.
            </div>
            <div className="flex space-x-6">
              <button className="text-gray-500 text-sm hover:text-blue-400 transition-colors">
                Terms of Service
              </button>
              <button className="text-gray-500 text-sm hover:text-blue-400 transition-colors">
                Privacy Policy
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default RustPage; 